import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import CloudIcon from "@mui/icons-material/Cloud";
import SecurityIcon from "@mui/icons-material/Security";
import CodeIcon from "@mui/icons-material/Code";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { Link } from "react-router";
import { DocumentHead } from "../../components/common/DocumentHead";
import { JsonLd } from "../../components/common/JsonLd";
import { ROUTES } from "../../config";
import { totalTemplateCount } from "../../data/template-registry";

const docsSections = [
  {
    id: "platform-overview",
    title: "Platform Overview",
    description: "How Rusha deploys from rusha.yml to running pods",
    icon: <ArchitectureIcon />,
    content: `Rusha is a manifest-driven deployment platform. You define your system in a rusha.yml file, push to Git, and Rusha handles the rest:

1. The GitHub webhook triggers the API to create a build job
2. Build-service clones your repo, builds Docker images via Kaniko, and pushes to GHCR
3. Deploy-service renders Kubernetes manifests from your template spec
4. Deploy-service commits to the GitOps repo, and ArgoCD auto-syncs
5. Your services are live with health checks, load balancing, and TLS

The manifest model means you always know what's deployed. Every change goes through Git, so you get full auditability and rollback via Git revert.`,
  },
  {
    id: "template-system",
    title: "Template System",
    description: "How templates work and how to choose one",
    icon: <CodeIcon />,
    content: `Templates are pre-configured infrastructure stacks with sensible defaults. Each template defines:

- Services: What containers run (UI, API, database, cache, etc.)
- Resources: CPU, memory, and storage requests
- Health checks: Liveness and readiness probes
- VCS config: How source code is built (Dockerfile or base image + build command)
- Stack config: Named components (frontend, backend, database, cache)
- Deployment modes: Where the template can run (Kubernetes, AWS, GCP, standalone)

When you create a system from a template, Rusha provisions a new GitHub repo with the starter code, sets up branch protection (dev/staging/main), and installs a deploy webhook. Pushing to any branch triggers a deployment to the matching environment.

Choose a template based on your use case: web applications for full-stack apps, data engineering for ELT/streaming pipelines, analytics for OLAP + BI, observability for monitoring, and databases for HA clusters.`,
  },
  {
    id: "deployment-modes",
    title: "Deployment Options",
    description: "Where you can deploy your systems",
    icon: <CloudIcon />,
    content: `Rusha supports multiple deployment targets:

- Rusha-hosted (kubernetes): Fully managed Kubernetes cluster with TLS, load balancing, and autoscaling. Zero infrastructure to manage.
- AWS (aws_services): Deploy to ECS, EKS, Lambda, or your own EC2 infrastructure. Supports managed services like RDS, ElastiCache, and MSK.
- GCP (gcp_services): Cloud Run, GKE, or custom infrastructure. BigQuery integration for analytics templates.
- Azure (azure_services): Container Apps, AKS, or custom infrastructure.
- Self-hosted (standalone): Any Kubernetes cluster. Bring your own ingress, cert manager, and storage classes.

Each template lists which deployment modes it supports. Some templates (like AWS-native ones with Redshift or Athena) only work with specific providers.`,
  },
  {
    id: "local-development",
    title: "Local Development",
    description: "How docker compose mirrors production",
    icon: <RocketLaunchIcon />,
    content: `Every template that includes a docker-compose.yml mirrors the production topology exactly. This means:

- Same base images as production (ghcr.io/rusha-corp/base-image-*)
- Same service names and ports
- Same environment variable structure
- Same health check endpoints

Run locally with:

  cd your-project
  docker compose up --build

This lets you test your application end-to-end before pushing to Git. The compose file uses the same database schemas, cache configurations, and API routes as production, so behavior matches exactly.

Templates without Docker Compose (like managed AWS services) provide specific local dev instructions in their starter README.`,
  },
  {
    id: "security",
    title: "Security & Isolation",
    description: "Security model and best practices",
    icon: <SecurityIcon />,
    content: `Rusha implements defense-in-depth security:

- Org-scoped JWT authentication: Two-step auth model. After login, you select an org to get an org-scoped token. API calls without org context return 403.
- Hierarchical RBAC: Permissions flow from org -> project -> system. Admin role implies all lower permissions.
- Namespace isolation: Each project gets its own Kubernetes namespace with network policies.
- Secrets management: Sealed Secrets encrypt all K8s secrets at rest. ArgoCD self-heal prevents manual secret edits.
- Image scanning: Trivy scans every image on deploy. Vulnerable images are blocked.
- Database split: Local dev uses rusha_dev database. Production services use the rusha database with separate credentials.`,
  },
];

export const DocsPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: 8 }}>
      <DocumentHead
        title="Documentation | Rusha"
        description="Learn how Rusha works. Platform overview, template system, deployment options, local development, and security model."
        path={ROUTES.DOCS}
      />
      <JsonLd
        data={{
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://rusha.io",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Documentation",
              item: "https://rusha.io/docs",
            },
          ],
        }}
      />
      <JsonLd
        data={{
          "@type": "TechArticle",
          headline: "Rusha Documentation",
          description:
            "Platform overview, template system, deployment options, local development, and security model.",
          url: "https://rusha.io/docs",
        }}
      />
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h2" component="h1" gutterBottom fontWeight={800}>
            Documentation
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 6 }}>
            Everything you need to know about the Rusha platform. Learn how
            templates work, where you can deploy, and how to develop locally.
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {docsSections.map((section, index) => (
            <Grid item xs={12} md={6} key={section.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper sx={{ p: 3, height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                      color: "primary.main",
                    }}
                  >
                    {section.icon}
                    <Typography variant="h2" component="h2" fontWeight={600}>
                      {section.title}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {section.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}
                  >
                    {section.content}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Quick Start */}
        <Paper
          sx={{
            p: 4,
            mt: 6,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <Typography variant="h2" component="h2" gutterBottom fontWeight={600}>
            Quick Start
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: "primary.contrastText" }} />
              </ListItemIcon>
              <ListItemText primary="Create an account and select your organization" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: "primary.contrastText" }} />
              </ListItemIcon>
              <ListItemText primary="Create a project and choose from one of the available templates" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: "primary.contrastText" }} />
              </ListItemIcon>
              <ListItemText primary="Rusha provisions a GitHub repo with starter code and deploy webhook" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: "primary.contrastText" }} />
              </ListItemIcon>
              <ListItemText primary="Push to dev, staging, or main to trigger automatic deployment" />
            </ListItem>
          </List>
          <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Chip
              label={`${totalTemplateCount} templates available`}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.15),
                color: "primary.contrastText",
              }}
            />
            <Link to="/templates" style={{ textDecoration: "none" }}>
              <Chip
                label="Browse Templates"
                clickable
                sx={{
                  bgcolor: alpha(theme.palette.common.white, 0.15),
                  color: "primary.contrastText",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.common.white, 0.25),
                  },
                }}
              />
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DocsPage;
